import { PercentPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FinoButtonModule } from '@fino-ui/button';
import { FinoInputModule } from '@fino-ui/input';
import { FinoTranslationModule } from '@fino-ui/translation';
import _ from 'lodash';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { v4 as uuidv4 } from 'uuid';
import { ConsecutiveZerosPipe } from '../../../core/pipes/consecutive-zeros.pipe';
import type {
  EnrichedFinances,
  EnrichedTransaction,
  FieldMetadata,
  TransactionType,
} from '../correction-tool.models';
import { CellEditorComponent } from './cell-editor/cell-editor.component';
import { getColumnsByTransactionType } from './correction-table.constants';
import { NewTableComponent } from './new-table/new-table.component';

@Component({
  selector: 'app-correction-table',
  imports: [
    FinoButtonModule,
    FinoTranslationModule,
    NgxTippyModule,
    PercentPipe,
    FinoInputModule,
    CellEditorComponent,
    NewTableComponent,
    ConsecutiveZerosPipe,
  ],
  templateUrl: './correction-table.component.html',
  styleUrl: './correction-table.component.sass',
})
export class CorrectionTableComponent {
  public currentPageNum = input.required<number>();
  public threshold = input.required<number>();
  public enrichedData = input<EnrichedFinances | null>(null);

  // Internal writable signal for mutations
  private _enrichedDataInternal = signal<EnrichedFinances | null>(null);

  public submitTable = output<EnrichedFinances>();

  public currentEditingField: FieldMetadata = null;

  totalTablesCount = signal(0);
  alreadySeenTables = signal(new Set<string>());

  @ViewChild('cellEditor') cellEditor?: CellEditorComponent;

  correctedTablesCount = computed(() => this.alreadySeenTables().size);
  progressPercent = computed(() => {
    const total = this.totalTablesCount();
    const corrected = this.correctedTablesCount();
    return total > 0 ? (corrected / total) * 100 : 0;
  });

  tablesOfCurrentPage = computed(() => {
    const enrichedData = this._enrichedDataInternal();
    const currentPageNum = this.currentPageNum();
    const tableMapping = enrichedData?._tableMapping || [];

    const allTablesToFinances = tableMapping.map((mapping) => {
      const finances = enrichedData[
        mapping.transactionType
      ] as EnrichedTransaction[];
      if (!finances) return { mapping, items: [] };

      const items = _.filter(
        finances,
        (f) => f._rowMetadata.sourceTableId === mapping.tableId,
      );
      return { mapping, items };
    });

    const allTableRowsOnCurrentPage = _.filter(
      allTablesToFinances,
      (t) => t.mapping.page === currentPageNum,
    ) as any;

    return _.map(
      _.uniqBy(allTableRowsOnCurrentPage, (row: any) => row.mapping.tableId),
      (table) => table,
    );
  });

  constructor() {
    // Sync internal signal with input
    effect(() => {
      this._enrichedDataInternal.set(this.enrichedData());
    });

    effect(() => {
      const enrichedData = this._enrichedDataInternal();
      if (enrichedData) {
        this.setupAlreadySeenTablesProgressBar();
      }
    });

    effect(() => {
      const currentPageNum = this.currentPageNum();
      if (currentPageNum) {
        this.cellEditor?.dismiss();
        this.markCurrentPageTablesAsSeen();
      }
    });
  }

  private markCurrentPageTablesAsSeen(): void {
    const enrichedData = this._enrichedDataInternal();
    if (!enrichedData) return;
    const tableMapping = enrichedData._tableMapping || [];
    for (const mapping of tableMapping) {
      if (mapping.page === this.currentPageNum()) {
        this.alreadySeenTables.update((set) =>
          new Set(set).add(mapping.tableId),
        );
      }
    }
  }

  setupAlreadySeenTablesProgressBar() {
    const enrichedData = this._enrichedDataInternal();
    this.totalTablesCount.set(enrichedData?._tableMapping?.length || 0);
  }

  getColumnsByTransactionType(table) {
    return table.mapping.columns;
  }

  getRows(table) {
    return table.items as EnrichedTransaction[];
  }

  makeFieldEditable(row, column) {
    // Don't allow editing ignored rows
    if (row.ignoreTransaction) return;

    if (this.currentEditingField) {
      this.currentEditingField.showInput = false;
    }

    if (!row._fieldMetadata) {
      row._fieldMetadata = {
        [column]: { confidence: 1 },
      };
    } else if (!row._fieldMetadata[column]) {
      row._fieldMetadata[column] = { confidence: 1 };
    }

    row._fieldMetadata[column].showInput = true;
    this.currentEditingField = row._fieldMetadata[column];
  }

  cellEdited(
    $event: {
      value?: string;
      dependantFields?: { key: string; value: string }[];
    },
    _table,
    row: EnrichedTransaction,
    column: string,
  ) {
    row._fieldMetadata[column].showInput = false;
    this.currentEditingField = null;

    if (!$event) return;

    row._fieldMetadata[column].edited = true;

    if ($event.value) row[column] = $event.value;
    _.forEach($event.dependantFields, (field) => {
      if (!row._fieldMetadata[field.key]) {
        row._fieldMetadata[field.key] = { confidence: 1 };
      }
      row._fieldMetadata[field.key].valueBeforeEditing = row[field.key];
      row[field.key] = field.value;
    });
  }

  removeRow(table, row: EnrichedTransaction): void {
    const { transactionType, tableId } = table.mapping;
    const enrichedData = this._enrichedDataInternal();

    if (enrichedData) {
      _.remove(
        enrichedData[transactionType],
        (i) =>
          i._rowMetadata?.rowIndex === row._rowMetadata?.rowIndex &&
          i._rowMetadata?.sourceTableId === tableId,
      );
      this._enrichedDataInternal.set({ ...enrichedData });
    }
  }

  getConfidence(row: any, column: string): number | null {
    return row?._fieldMetadata?.[column]?.confidence ?? null;
  }

  getMetadataForField(row, column) {
    return row?._fieldMetadata?.[column];
  }

  addNewRow(currentTable): void {
    const transactionType = currentTable.mapping.transactionType;
    const enrichedData = this._enrichedDataInternal();

    // at least name is mandatory, otherwise group by will remove empty object
    const newItem: any = {
      name: '',
      _rowMetadata: {
        sourcePage: this.currentPageNum(),
        sourceTableId: currentTable.mapping.tableId,
        rowIndex: uuidv4(),
      },
    };

    if (enrichedData) {
      enrichedData[transactionType].push(newItem);
      // Trigger signal update
      this._enrichedDataInternal.set({ ...enrichedData });
    }
    currentTable.items.push(newItem);
  }

  addNewTable(transactionType: TransactionType) {
    const enrichedData = this._enrichedDataInternal();
    const mapping = {
      transactionType,
      page: this.currentPageNum(),
      tableId: `byCorrectorAdded-${uuidv4()}`,
      columns: getColumnsByTransactionType(transactionType),
    };
    const newTable = {
      mapping,
      items: [],
    };

    if (enrichedData) {
      enrichedData._tableMapping.push(mapping);
      // Trigger signal update
      this._enrichedDataInternal.set({ ...enrichedData });
    }
    this.addNewRow(newTable);
  }

  // Submission
  submitCorrection(): void {
    const enrichedData = this._enrichedDataInternal();
    this.submitTable.emit(enrichedData);

    this.alreadySeenTables.set(new Set());
  }
}
